import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../../utilities/axios';
import * as API_END_POINT from '../../utilities/constant';
import moment from 'moment';

import SideFilter from '../../components/Common/SideFilter';
import FilterPills from '../../components/Common/Dropdowns/FilterPills';
import logoPng from '../../assets/images/logo-png.png';

import {
    Browse,
    ProductCard,
    GridSkeleton,
    BrandAbout,
    BrandAboutSkeleton,
    BrandHead,
    BrandStory,
    Pager,
    ShareMeta,
    firstImageOf,
} from '../../shared/components';
import { useStore } from '../../context/StoreContext';
import { RECOMMENDED, STOREFRONT_PRODUCT_SORTS, API_PRODUCT_SORT } from '../../utilities/productSorts';
import { useStorefrontTheme } from '../../shared/theme/storefrontTheme';

const BrandDetails = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [manualPriceRange, setManualPriceRange] = useState({ min: '', max: '' });
    const [brandInfo, setBrandInfo] = useState({});
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 10 });
    const [products, setProducts] = useState([]);
    const { id, vendorId } = useParams();
    const [filtersView, setfiltersView] = useState([]);
    const [filters, setFilters] = useState({
        vendorValues: [],
        brandValues: [],
        valueSearch: '',
        priceRange: [],
        animal_type: [],
        size: [],
        life_stage: [],
        tags: [],
        categories: [],
        discounts: []
    });
    const [sortBy, setSortBy] = useState(RECOMMENDED);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [limit] = useState(100);
    /* The page belongs to the filters and sort it was turned on: changing
       either starts again at page 1. Derived rather than reset in an effect, so
       the change fetches once — same pattern as `/all-products`. */
    const queryKey = `${JSON.stringify(filters)}|${sortBy}`;
    const [pageOf, setPageOf] = useState({ key: queryKey, page: 1 });
    const currentPage = pageOf.key === queryKey ? pageOf.page : 1;
    const offset = (currentPage - 1) * limit;
    const handlePageChange = (page) => {
        setPageOf({ key: queryKey, page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    // The storefront sort list, minus Brand: every product here is the same brand.
    const sortOptions = STOREFRONT_PRODUCT_SORTS.filter((option) => API_PRODUCT_SORT[option]);

    const cancelReq = useRef(null);
    const latestReqRef = useRef(0);
    const fetchProductList = async () => {
        setIsLoading(true);
        latestReqRef.current += 1;
        const requestId = latestReqRef.current;

        // Cancel previous request if it exists
        if (cancelReq?.current) {
            cancelReq?.current?.cancel("Cancelled due to new request"); // now works
        }

        // Create new cancel token SOURCE (IMPORTANT)
        const source = axios.CancelToken.source();
        cancelReq.current = source;
        try {
            const filterArr = [];
            const searchKeyword = searchTerm;
            if (filters.priceRange && filters.priceRange.length > 0) {
                filters.priceRange.map((value) => {
                    const obj = {
                        field: 'pricerange',
                        options: {
                            min: value.min,
                            max: value.max
                        }
                    };
                    filterArr.push(obj);
                })
            }
            if (filters.brandValues && filters.brandValues.length > 0) {
                const obj = {
                    field: 'brands',
                    options: {
                        operator: "in",
                        values: filters.brandValues.map((e) => e.toString())
                    }
                };
                filterArr.push(obj);
            }

            if (filters.categories && filters.categories.length > 0) {
                let subcategory = [];

                filters.categories?.map((category) => {
                    if (category?.subcategory?.length > 0) {
                        subcategory.push(...category?.subcategory)
                    }
                })
                const obj = {
                    field: 'subcategory',
                    options: {
                        operator: "in",
                        values: subcategory.map((e) => e.toString())
                    }
                };
                filterArr.push(obj);
            }
            if (filters.tags && filters.tags.length > 0) {
                const obj = {
                    field: 'tags',
                    options: {
                        operator: "in",
                        values: filters.tags.map((e) => e.toString())
                    }
                };
                filterArr.push(obj);
            }


            const sortArr = API_PRODUCT_SORT[sortBy] || API_PRODUCT_SORT[RECOMMENDED];

            let API_URL = `${API_END_POINT.RETAILER_BRAND_PRODUCT_LIST(id)}`;

            if (vendorId) {
                API_URL = `${API_END_POINT.RETAILER_VENDOR_PRODUCT_LIST(id, vendorId)}`;
            }

            const response = await axios.post(
                `${API_URL}`,
                {
                    paging: { limit: limit, offset: offset },
                    sort: sortArr,
                    ...(filterArr.length ? { filter: filterArr } : null),
                    ...(searchKeyword ? { query: { searchKeyword } } : {})
                },
                {
                    cancelToken: source.token, // IMPORTANT
                }
            );
            
            // Ignore outdated responses
            if (requestId !== latestReqRef.current) return;

            setProducts(response.data.data.items);
            setBrandInfo(response.data.data.brandInfo);
            setPagination(response.data.data.pagination)
        } catch (error) {
            console.error('Error fetching brands:', error);
        } finally {
            // Only the latest successful request controls loading
            if (requestId === latestReqRef.current) {
                setTimeout(()=>{
                    setIsLoading(false);
                }, [1500])
            }
        }
    };
    useEffect(() => {
        fetchProductList();
    }, [id])

    useEffect(() => {
        fetchProductList();
    /* `activeTab` is gone from this dependency array with the tabs: it only
       ever refetched the identical product list when the shopper moved to Brand
       info and back. Nothing else changed. */
    }, [filters, sortBy, currentPage, offset]);

    useEffect(() => {
        getBrandProductsFilter();
    }, [])
    const [brandProductFilters, setBrandProductFilters] = useState(null);
    const getBrandProductsFilter = async () => {
        const response = await axios.post(
            `${API_END_POINT.STORE_BRAND_PRODUCT_FILTERS(id)}`,
            {}
        );
        setBrandProductFilters(response.data.data?.availableFilters)
    }
    const clearFilters = () => {
        setManualPriceRange({min: '', max: ''})
        setFilters({
            vendorValues: [],
            brandValues: [],
            valueSearch: '',
            priceRange: [],
            animal_type: [],
            size: [],
            life_stage: [],
            tags: [],
            categories: [],
            discounts: []
        });
        setSearchParams({});
    };
    useEffect(() => {
        // getAllProductsFilter();
        // Only clear filters if there are no URL parameters at all
        if (!searchParams.get('category') && !searchParams.get('brands') && !searchParams.get('vendors') && !searchParams.get('brand_values') && !searchParams.get('hassample') && !searchParams.get('price') && !searchParams.get('animal_type') && !searchParams.get('life_stage_age') && !searchParams.get('specialty_diet') && !searchParams.get('size_breed') && !searchParams.get('intended_use')) {
            clearFilters();
            setfiltersView([])
        }
    }, [setSearchParams])
    /* The partner owns the skin: `.sfx` resolves the static tokens, but --o
       (anything clickable) and --m (header and banner) come from the saved
       Style config and have to be written onto the element. */
    const { pageDetails } = useStore();
    const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);

    /* The grid waits for the products AND the facets: a full grid beside an
       empty rail reads as a brand with no filters. */
    const isBusy = isLoading || !brandProductFilters;
    const brandName = brandInfo?.store_name;
    const brandLogo = brandInfo?.store_logo || brandInfo?.profile_picture;

    /* Vimeo and YouTube only, anything else passed through. Unchanged from the
       page's own helper. */
    const getPlayableVideoUrl = (url) => {
        if (!url) return '';
        const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
        const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
        if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
        return url;
    };

    const crumb = [
        { label: 'Home', href: '/' },
        { label: 'Brands', href: '/all-brands' },
        { label: brandName || '' },
    ];

    /* The facts line is the brand's values, and nothing is invented to pad it:
       `brandInfo` carries the values but no city/state/country field, so the
       location fact is absent rather than a placeholder. The row takes any
       number of facts, so add it the day the API grows one.

       The values are not repeated below: `BrandAbout` renders its own "Brand
       values" section, and passing `values` there too would print the same
       strings twice. The hero is where the reference puts them. */
    const brandFacts = (brandInfo?.brandValues || []).map((v) => v.name).filter(Boolean);

    const appliedCount = filtersView?.length || 0;

    /* No PageLayout: it wraps children in `max-w-7xl mx-auto` and renders its
       own breadcrumb and heading, while `.br-wrap` is 1240px with a 40px gutter
       and owns all three. Same call `/all-products` and `/all-brands` made. */
    return (
        <div ref={sfxRef} className="sfx">
            <ShareMeta title={brandName} image={brandLogo || firstImageOf(products)} />

            {/* The crumb and the header are drawn here, not by `Browse`, for a
                structural reason: the reference's order is crumb → identity
                header → hairline → toolbar, and `Browse` renders its <h1> right
                after its crumb with no slot between. Handing it the whole header
                as `title` put a <div> inside an <h1> and made that heading's
                accessible name the entire block.

                So the page owns the top: `.bd-wrap` is the same 1240px column,
                and `Browse` gets neither `title` nor `crumb` (the empty <h1> it
                still renders is hidden in `brandHeader.css`). A `headerSlot`
                prop on `Browse` would delete all of this — flagged to that
                shared component's owner. */}
            <div className="bd-wrap">
                <nav className="br-crumb" aria-label="Breadcrumb">
                    {crumb.map((c, i) => (
                        <React.Fragment key={`${c.label}-${i}`}>
                            {i > 0 && ' / '}
                            {c.href ? <a href={c.href}>{c.label}</a> : <span>{c.label}</span>}
                        </React.Fragment>
                    ))}
                </nav>

                {/* No tagline is passed, deliberately: the reference's `.sb-line`
                    is a short `desc` that exists alongside the longer `story`,
                    but `brandInfo` has only `brand_story`, which is already the
                    Overview below. The row is wired and waiting for a real
                    short-description field. */}
                <BrandHead
                    name={brandName}
                    logo={brandLogo}
                    fallbackLogo={logoPng}
                    facts={brandFacts}
                    loading={!brandName}
                />
            </div>

                <Browse
                    noun="products"
                    total={pagination?.totalItems}
                    items={isBusy ? [] : products}
                    loading={isBusy}
                    loadingView={<GridSkeleton count={9} />}
                    searchValue={searchTerm}
                    searchPlaceholder={brandName ? `Search ${brandName}` : 'Search products'}
                    onSearchChange={(val) => {
                        setSearchTerm(val);
                        setPageOf({ key: queryKey, page: 1 });
                    }}
                    sorts={sortOptions.map((o) => [o, o])}
                    sortValue={sortBy}
                    onSortChange={setSortBy}
                    chipsSlot={
                        appliedCount > 0 ? (
                            <FilterPills
                                filterData={brandProductFilters}
                                setManualPriceRange={setManualPriceRange}
                                clearFilters={clearFilters}
                                filtersView={filtersView}
                                setfiltersView={setfiltersView}
                                setFilters={setFilters}
                            />
                        ) : null
                    }
                    chipCount={appliedCount}
                    rail={
                        <SideFilter
                            setManualPriceRange={setManualPriceRange}
                            manualPriceRange={manualPriceRange}
                            filterData={brandProductFilters}
                            filtersView={filtersView}
                            setfiltersView={setfiltersView}
                            filters={filters}
                            setFilters={setFilters}
                        />
                    }
                    emptyTitle="Nothing matches those filters"
                    emptyBody="Try removing one, or clear them all to see everything this brand makes."
                    onClearAll={clearFilters}
                    renderItem={(product) => {
                        /* The main image first; the rest are the product page's. */
                        const images = Array.isArray(product.product_images) ? product.product_images : [];
                        const main = [...images].sort((a, b) => Number(b.is_main) - Number(a.is_main))[0];
                        const brand = product?.is_multi_brand
                            ? product.vendor?.name || product.shopify_vendor?.shopdot_vendor?.display_name
                            : product.brand?.name || product.brand_name;

                        return (
                            <ProductCard
                                name={product.title}
                                brand={brand}
                                price={product.price_msrp}
                                image={main?.src}
                                /* A real href, so the card is a real link:
                                   keyboard reachable, announced, and openable in
                                   a new tab. It was a <div onClick>. */
                                href={`/product/${product.id}`}
                                onClick={(e) => {
                                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                    e.preventDefault();
                                    navigate(`/product/${product.id}`);
                                }}
                                /* No brandHref on a brand's own page — the link
                                   would point at the page the shopper is on. */
                                /* `contain`, not the reference's `cover`: this
                                   catalogue carries packshots and `cover` crops
                                   them. Same call as `/all-products`; flip both
                                   together. */
                                objectFit="contain"
                            />
                        );
                    }}
                    /* Everything about the brand sits under the products, which
                       is the reference's own instruction ("The story sits UNDER
                       the products, in the results column, and is absent when
                       there is none." — storefront.css l.914, `.sb-why`).
                       `belowGrid` renders inside the results column, so the
                       shared component needed no change.

                       Products come first because the reference deleted its photo
                       hero for putting the first product 838px down the page, and
                       a tab row is worse still.

                       Shipping and Return & refund policy stay OFF this page
                       (confirmed with the product owner): they belong to the
                       product drawer's About tab. Connect stays — a brand landing
                       page being contactable is worth keeping.

                       The pager comes first: it belongs to the grid above it. */
                    belowGrid={
                        !brandName ? (
                            <BrandAboutSkeleton />
                        ) : (
                            <>
                                {!isBusy && (
                                    <Pager
                                        page={currentPage}
                                        perPage={limit}
                                        total={pagination?.totalItems}
                                        onPageChange={handlePageChange}
                                    />
                                )}
                                <BrandStory name={brandName} story={brandInfo?.brand_story} />
                                <BrandAbout
                                    name={brandName}
                                    valuesNote={brandInfo?.charitable_or_community_prog}
                                    videoSrc={getPlayableVideoUrl(brandInfo?.brand_promo)}
                                    videoPoster={brandLogo || logoPng}
                                    onPosterError={(e) => { e.target.src = logoPng; }}
                                    /* The year, not the date: the reference's
                                       fact reads "On ShopDot since 2024". */
                                    since={brandInfo?.created_at ? moment(brandInfo.created_at).format('YYYY') : ''}
                                    email={brandInfo?.company_email_address}
                                />
                            </>
                        )
                    }
                />
        </div>
    );
}

export default BrandDetails
